import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import authApi from '../services/authApi';
import verificationService from '../services/verificationService';
import { errorHandler } from '../utils/errorHandler';
import { useRouter } from 'vue-router';
import config from '../config.js';

/**
 * Authentication store for managing user authentication state
 */
export const useAuthStore = defineStore('auth', () => {
  const router = useRouter();
  
  // State
  const user = ref(null);
  const accessToken = ref(localStorage.getItem('accessToken') || null);
  const refreshToken = ref(localStorage.getItem('refreshToken') || null);
  const isLoading = ref(false);
  const error = ref(null);

  // Getters
  const isAuthenticated = computed(() => !!user.value && !!accessToken.value);
  const currentUser = computed(() => user.value);
  const authError = computed(() => {
    const errorValue = error.value;
    console.log('[DEBUG] AuthStore.authError computed: Accessed', {
      errorValue,
      errorType: typeof errorValue,
      isNull: errorValue === null,
      isUndefined: errorValue === undefined,
      isEmpty: errorValue === '',
      stackTrace: new Error().stack
    });
    return errorValue;
  });

  // Actions
  const setTokens = (tokens) => {
    console.log('[DEBUG] AuthStore.setTokens: Called with:', {
      hasTokens: !!tokens,
      hasAccessToken: !!tokens?.accessToken,
      hasRefreshToken: !!tokens?.refreshToken,
      accessTokenPreview: tokens?.accessToken ? tokens.accessToken.substring(0, 20) + '...' : null,
      refreshTokenPreview: tokens?.refreshToken ? tokens.refreshToken.substring(0, 20) + '...' : null
    });
    
    if (!tokens) {
      // Clear tokens if null/undefined
      console.log('[DEBUG] AuthStore.setTokens: Tokens is null/undefined, clearing tokens');
      clearTokens();
      return;
    }
    
    if (tokens.accessToken) {
      accessToken.value = tokens.accessToken;
      localStorage.setItem('accessToken', tokens.accessToken);
      const storedToken = localStorage.getItem('accessToken');
      console.log('[DEBUG] AuthStore.setTokens: AccessToken stored to localStorage:', {
        stored: !!storedToken,
        matches: storedToken === tokens.accessToken,
        storedPreview: storedToken ? storedToken.substring(0, 20) + '...' : null
      });
    } else {
      console.warn('[DEBUG] AuthStore.setTokens: No accessToken in tokens object');
    }
    
    if (tokens.refreshToken) {
      refreshToken.value = tokens.refreshToken;
      localStorage.setItem('refreshToken', tokens.refreshToken);
      const storedRefreshToken = localStorage.getItem('refreshToken');
      console.log('[DEBUG] AuthStore.setTokens: RefreshToken stored to localStorage:', {
        stored: !!storedRefreshToken,
        matches: storedRefreshToken === tokens.refreshToken,
        storedPreview: storedRefreshToken ? storedRefreshToken.substring(0, 20) + '...' : null
      });
    } else {
      console.warn('[DEBUG] AuthStore.setTokens: No refreshToken in tokens object');
    }
    
    // Final verification
    const finalAccessToken = localStorage.getItem('accessToken');
    const finalRefreshToken = localStorage.getItem('refreshToken');
    console.log('[DEBUG] AuthStore.setTokens: Final localStorage state:', {
      accessTokenPresent: !!finalAccessToken,
      refreshTokenPresent: !!finalRefreshToken,
      accessTokenValue: accessToken.value ? accessToken.value.substring(0, 20) + '...' : null
    });
  };

  const clearTokens = () => {
    accessToken.value = null;
    refreshToken.value = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  };

  const setUser = (userData) => {
    user.value = userData;
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const clearUser = () => {
    user.value = null;
    localStorage.removeItem('user');
  };

  const setError = (err) => {
    console.log('[DEBUG] AuthStore.setError: Called', {
      err,
      errType: typeof err,
      errIsObject: err && typeof err === 'object',
      hasGetUserMessage: err && typeof err.getUserMessage === 'function',
      errMessage: err?.message,
      currentErrorValue: error.value,
      stackTrace: new Error().stack
    });
    
    try {
      // Handle null/undefined - clear the error
      if (err === null || err === undefined) {
        console.log('[DEBUG] AuthStore.setError: Clearing error (null/undefined)');
        error.value = null;
        console.log('[DEBUG] AuthStore.setError: Error cleared, error.value =', error.value);
        return;
      }
      
      // Handle structured errors from errorHandler
      if (err && typeof err === 'object') {
        console.log('[DEBUG] AuthStore.setError: Handling as object');
        if (typeof err.getUserMessage === 'function') {
          console.log('[DEBUG] AuthStore.setError: Has getUserMessage method, calling it');
          const userMessage = err.getUserMessage();
          console.log('[DEBUG] AuthStore.setError: getUserMessage returned:', {
            userMessage,
            type: typeof userMessage,
            isString: typeof userMessage === 'string',
            isEmpty: !userMessage || (typeof userMessage === 'string' && !userMessage.trim())
          });
          if (userMessage && typeof userMessage === 'string' && userMessage.trim()) {
            console.log('[DEBUG] AuthStore.setError: Setting error.value to userMessage:', userMessage);
            error.value = userMessage;
            console.log('[DEBUG] AuthStore.setError: error.value set to:', error.value);
          } else {
            // getUserMessage returned empty or invalid value, fallback to message
            console.log('[DEBUG] AuthStore.setError: getUserMessage returned empty, falling back to err.message:', err.message);
            error.value = err.message || 'An error occurred';
            console.log('[DEBUG] AuthStore.setError: error.value set to (fallback):', error.value);
          }
        } else if (err.message) {
          console.log('[DEBUG] AuthStore.setError: No getUserMessage, using err.message:', err.message);
          error.value = err.message;
          console.log('[DEBUG] AuthStore.setError: error.value set to (err.message):', error.value);
        } else {
          console.log('[DEBUG] AuthStore.setError: No getUserMessage and no err.message, using generic error');
          error.value = 'An error occurred';
          console.log('[DEBUG] AuthStore.setError: error.value set to (generic):', error.value);
        }
      } else if (typeof err === 'string' && err.trim()) {
        console.log('[DEBUG] AuthStore.setError: Handling as string:', err);
        error.value = err;
        console.log('[DEBUG] AuthStore.setError: error.value set to (string):', error.value);
      } else if (err?.message) {
        console.log('[DEBUG] AuthStore.setError: Handling err?.message:', err.message);
        error.value = err.message;
        console.log('[DEBUG] AuthStore.setError: error.value set to (err?.message):', error.value);
      } else {
        console.log('[DEBUG] AuthStore.setError: Falling through to generic error - no details available');
        error.value = 'An unexpected error occurred - no details available';
        console.log('[DEBUG] AuthStore.setError: error.value set to (fallback generic):', error.value);
      }
    } catch (error) {
      console.error('[DEBUG] AuthStore.setError: Exception in setError:', error);
      error.value = 'An unexpected error occurred - error processing failed';
      console.log('[DEBUG] AuthStore.setError: error.value set to (exception handler):', error.value);
    }
    
    console.log('[DEBUG] AuthStore.setError: Final error.value:', error.value);
    console.log('[DEBUG] AuthStore.setError: authError computed would return:', error.value);
    
    // Don't auto-clear errors - let user dismiss them
    // Errors will be cleared:
    // 1. On successful login/registration
    // 2. When user switches mode (login ↔ register) via resetForm()
    // 3. When user closes modal via resetForm()
    // 4. Explicitly set to null via setError(null)
  };

  /**
   * Initialize auth state from localStorage
   */
  const initialize = async () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        user.value = JSON.parse(storedUser);
      }

      // Validate token if exists
      if (accessToken.value) {
        try {
          await authApi.getCurrentUser();
        } catch (err) {
          // Token might be expired, try to refresh
          if (refreshToken.value) {
            await refresh();
          } else {
            clearAuth();
          }
        }
      }
    } catch (err) {
      console.error('Failed to initialize auth:', err);
      clearAuth();
    }
  };

  /**
   * Register a new user
   */
  const register = async (userData) => {
    console.log('[DEBUG] register called with userData:', userData);
    isLoading.value = true;
    error.value = null;

    try {
      console.log('[DEBUG] Calling authApi.register...');
      const response = await authApi.register(userData);
      console.log('[DEBUG] authApi.register response:', response);
      
      // Feature flag for new verification flow
      const useNewVerificationFlow = config.features?.emailVerificationFix || false;
      console.log('[DEBUG] useNewVerificationFlow:', useNewVerificationFlow);
      console.log('[DEBUG] response.tokens:', response.tokens);
      console.log('[DEBUG] config.features:', config.features);
      
      if (useNewVerificationFlow) {
        console.log('[DEBUG] Using NEW FLOW');
        // NEW FLOW: Only set tokens if user is verified or verification is disabled
        if (response.tokens) {
          console.log('[DEBUG] Setting tokens and user');
          setTokens(response.tokens);
          setUser(response.user);
        } else {
          console.log('[DEBUG] Setting user only (no tokens)');
          // Store user data without tokens for verification flow
          setUser(response.user);
        }
      } else {
        console.log('[DEBUG] Using OLD FLOW');
        // OLD FLOW: Maintain backward compatibility
        setTokens(response.tokens);
        setUser(response.user);
      }
      
      // Check if verification is needed
      const needsVerification = verificationService.userNeedsVerification(response.user);
      console.log('[DEBUG] needsVerification:', needsVerification);
      
      return {
        success: true,
        message: response.message,
        user: response.user,
        needsVerification,
        verificationMessage: needsVerification 
          ? (response.emailQueued 
            ? 'Your verification email is in the queue. You will receive it when quota is available.'
            : 'Please check your email to verify your account.')
          : null,
        emailQueued: response.emailQueued || false,
        queuePosition: response.queuePosition || null,
        estimatedWaitTime: response.estimatedWaitTime || null
      };
    } catch (err) {
      console.error('[DEBUG] Error in register:', err);
      console.error('[DEBUG] Error type:', typeof err);
      console.error('[DEBUG] Error properties:', Object.keys(err));
      console.error('[DEBUG] Error message:', err.message);
      console.error('[DEBUG] Error stack:', err.stack);
      
      const structuredError = errorHandler.handleError(err, { action: 'register' });
      console.error('[DEBUG] structuredError:', structuredError);
      console.error('[DEBUG] structuredError.getUserMessage:', structuredError.getUserMessage);
      console.error('[DEBUG] structuredError.message:', structuredError.message);
      
      setError(structuredError);
      return {
        success: false,
        error: structuredError.getUserMessage ? structuredError.getUserMessage() : structuredError.message
      };
    } finally {
      isLoading.value = false;
      console.log('[DEBUG] register completed, isLoading set to false');
    }
  };

  /**
   * Login user
   */
  const login = async (identifier, password) => {
    console.log('[DEBUG] AuthStore.login: Called', { identifier, hasPassword: !!password });
    isLoading.value = true;
    error.value = null;
    console.log('[DEBUG] AuthStore.login: Initialized, error cleared, isLoading=true');

    try {
      console.log('[DEBUG] AuthStore.login: Calling authApi.login');
      const response = await authApi.login(identifier, password);
      console.log('[DEBUG] AuthStore.login: API response received', {
        hasTokens: !!response.tokens,
        hasUser: !!response.user,
        message: response.message,
        responseKeys: Object.keys(response)
      });
      
      setTokens(response.tokens);
      setUser(response.user);
      
      // Check if verification is needed
      const needsVerification = verificationService.userNeedsVerification(response.user);
      
      const result = {
        success: true,
        message: response.message,
        user: response.user,
        needsVerification,
        verificationMessage: needsVerification 
          ? 'Please verify your email to access all features.' 
          : null
      };
      console.log('[DEBUG] AuthStore.login: Returning success result', result);
      return result;
    } catch (err) {
      console.log('[DEBUG] AuthStore.login: Error caught', {
        errorType: typeof err,
        isAxiosError: err.isAxiosError,
        hasResponse: !!err.response,
        responseStatus: err.response?.status,
        responseData: err.response?.data,
        errorMessage: err.message,
        errorKeys: Object.keys(err)
      });
      
      const structuredError = errorHandler.handleError(err, { action: 'login' });
      console.log('[DEBUG] AuthStore.login: Structured error created', {
        errorType: structuredError.type,
        errorMessage: structuredError.message,
        hasGetUserMessage: typeof structuredError.getUserMessage === 'function',
        getUserMessageResult: structuredError.getUserMessage ? structuredError.getUserMessage() : 'N/A',
        structuredErrorKeys: Object.keys(structuredError)
      });
      
      console.log('[DEBUG] AuthStore.login: Calling setError with structured error');
      setError(structuredError);
      console.log('[DEBUG] AuthStore.login: After setError, error.value =', error.value);
      
      const userMessage = structuredError.getUserMessage ? structuredError.getUserMessage() : structuredError.message;
      const result = {
        success: false,
        error: userMessage
      };
      console.log('[DEBUG] AuthStore.login: Returning error result', result);
      return result;
    } finally {
      isLoading.value = false;
      console.log('[DEBUG] AuthStore.login: Finally block, isLoading=false, error.value=', error.value);
    }
  };

  /**
   * Logout user
   */
  const logout = async () => {
    isLoading.value = true;

    try {
      if (accessToken.value) {
        await authApi.logout(accessToken.value);
      }
    } catch (err) {
      console.error('Logout error:', err);
      // Continue with local logout even if server logout fails
    } finally {
      clearAuth();
      isLoading.value = false;
      
      // Redirect to home page
      router.push('/');
    }
  };

  /**
   * Refresh access token
   */
  const refresh = async () => {
    if (!refreshToken.value) {
      clearAuth();
      return false;
    }

    try {
      const response = await authApi.refreshToken(refreshToken.value);
      setTokens(response.tokens);
      return true;
    } catch (err) {
      console.error('Token refresh failed:', err);
      clearAuth();
      return false;
    }
  };

  /**
   * Get current user profile
   */
  const getCurrentUser = async () => {
    try {
      const userData = await authApi.getCurrentUser();
      setUser(userData);
      return userData;
    } catch (err) {
      console.error('Failed to get current user:', err);
      throw err;
    }
  };

  /**
   * Update user profile
   */
  const updateProfile = async (profileData) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authApi.updateProfile(profileData);
      setUser(response.user);
      
      return {
        success: true,
        message: response.message,
        user: response.user
      };
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Profile update failed');
      return {
        success: false,
        error: err.response?.data?.error || err.message || 'Profile update failed'
      };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Change password
   */
  const changePassword = async (currentPassword, newPassword) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authApi.changePassword(currentPassword, newPassword);
      
      return {
        success: true,
        message: response.message
      };
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Password change failed');
      return {
        success: false,
        error: err.response?.data?.error || err.message || 'Password change failed'
      };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Request password reset
   */
  const requestPasswordReset = async (email) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authApi.requestPasswordReset(email);
      
      return {
        success: true,
        message: response.message
      };
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Password reset request failed');
      return {
        success: false,
        error: err.response?.data?.error || err.message || 'Password reset request failed'
      };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Reset password with token
   */
  const resetPassword = async (token, newPassword) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authApi.resetPassword(token, newPassword);
      
      return {
        success: true,
        message: response.message
      };
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Password reset failed');
      return {
        success: false,
        error: err.response?.data?.error || err.message || 'Password reset failed'
      };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Verify email with token
   */
  const verifyEmail = async (token) => {
    isLoading.value = true;
    error.value = null;

    try {
      const result = await verificationService.verifyEmail(token);
      
      if (result.success) {
        // Update user verification status
        if (user.value) {
          user.value = {
            ...user.value,
            emailVerified: true,
            verificationToken: null
          };
          setUser(user.value);
        }
        
        return {
          success: true,
          message: result.message,
          user: result.user
        };
      } else {
        setError(result.error);
        return {
          success: false,
          error: result.error,
          code: result.code
        };
      }
    } catch (err) {
      const structuredError = errorHandler.handleError(err, { action: 'verifyEmail' });
      setError(structuredError);
      return {
        success: false,
        error: structuredError.getUserMessage ? structuredError.getUserMessage() : structuredError.message
      };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Resend verification email
   */
  const resendVerificationEmail = async (email) => {
    isLoading.value = true;
    error.value = null;

    try {
      const result = await verificationService.resendVerificationEmail(email);
      
      if (result.success) {
        return {
          success: true,
          message: result.message,
          nextResendAvailable: result.nextResendAvailable
        };
      } else {
        setError(result.error);
        return {
          success: false,
          error: result.error,
          code: result.code,
          nextResendAvailable: result.nextResendAvailable
        };
      }
    } catch (err) {
      const structuredError = errorHandler.handleError(err, { action: 'resendVerificationEmail' });
      setError(structuredError);
      return {
        success: false,
        error: structuredError.getUserMessage ? structuredError.getUserMessage() : structuredError.message
      };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Check verification status
   */
  const checkVerificationStatus = async (userId = null) => {
    isLoading.value = true;
    error.value = null;

    try {
      const result = await verificationService.getVerificationStatus(userId);
      
      if (result.success) {
        return {
          success: true,
          status: result.status
        };
      } else {
        setError(result.error);
        return {
          success: false,
          error: result.error,
          code: result.code
        };
      }
    } catch (err) {
      const structuredError = errorHandler.handleError(err, { action: 'checkVerificationStatus' });
      setError(structuredError);
      return {
        success: false,
        error: structuredError.getUserMessage ? structuredError.getUserMessage() : structuredError.message
      };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Check if current user needs verification
   */
  const needsVerification = computed(() => {
    if (!user.value) return false;
    return verificationService.userNeedsVerification(user.value);
  });

  /**
   * Get time until next resend is available
   */
  const getTimeUntilNextResend = (nextResendAvailable) => {
    return verificationService.getTimeUntilNextResend(nextResendAvailable);
  };

  /**
   * Process verification from URL (for email links)
   */
  const processVerificationFromUrl = async (token) => {
    isLoading.value = true;
    error.value = null;

    try {
      const result = await verificationService.processVerificationFromUrl(token);
      
      if (result.success) {
        // Update user if auto-login is enabled
        if (result.autoLogin && result.user) {
          setUser(result.user);
        }
        
        return {
          success: true,
          message: result.message,
          user: result.user,
          autoLogin: result.autoLogin
        };
      } else {
        setError(result.error);
        return {
          success: false,
          error: result.error,
          code: result.code,
          requiresNewToken: result.requiresNewToken
        };
      }
    } catch (err) {
      const structuredError = errorHandler.handleError(err, { action: 'processVerificationFromUrl' });
      setError(structuredError);
      return {
        success: false,
        error: structuredError.getUserMessage ? structuredError.getUserMessage() : structuredError.message,
        requiresNewToken: true
      };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Verify email with code and auto-login
   * @param {string} code - 6-digit verification code
   * @param {string} email - User email address
   */
  const verifyCodeAndLogin = async (code, email) => {
    console.log('[DEBUG] AuthStore.verifyCodeAndLogin: Starting verification');
    console.log('[DEBUG] AuthStore.verifyCodeAndLogin: Current localStorage state:', {
      accessTokenBefore: !!localStorage.getItem('accessToken'),
      refreshTokenBefore: !!localStorage.getItem('refreshToken')
    });
    
    isLoading.value = true;
    error.value = null;

    try {
      const result = await verificationService.verifyCode(code, email);
      console.log('[DEBUG] AuthStore.verifyCodeAndLogin: Verification result received:', {
        success: result.success,
        hasTokens: !!result.tokens,
        hasAccessToken: !!result.tokens?.accessToken,
        hasRefreshToken: !!result.tokens?.refreshToken,
        tokensPreview: result.tokens ? {
          accessToken: result.tokens.accessToken?.substring(0, 20) + '...',
          refreshToken: result.tokens.refreshToken?.substring(0, 20) + '...'
        } : null
      });
      
      if (result.success) {
        // Set tokens and user (auto-login)
        console.log('[DEBUG] AuthStore.verifyCodeAndLogin: Calling setTokens with:', {
          accessToken: result.tokens?.accessToken?.substring(0, 20) + '...',
          refreshToken: result.tokens?.refreshToken?.substring(0, 20) + '...'
        });
        setTokens(result.tokens);
        
        // Verify tokens were stored after setTokens
        const tokenAfterSet = localStorage.getItem('accessToken');
        const refreshTokenAfterSet = localStorage.getItem('refreshToken');
        console.log('[DEBUG] AuthStore.verifyCodeAndLogin: Token state after setTokens:', {
          accessTokenStored: !!tokenAfterSet,
          refreshTokenStored: !!refreshTokenAfterSet,
          accessTokenMatches: tokenAfterSet === result.tokens?.accessToken,
          accessTokenValue: tokenAfterSet ? tokenAfterSet.substring(0, 20) + '...' : null
        });
        
        setUser(result.user);
        
        return {
          success: true,
          message: result.message,
          user: result.user,
          tokens: result.tokens
        };
      } else {
        setError(result.error);
        return {
          success: false,
          error: result.error,
          code: result.code,
          attemptsRemaining: result.attemptsRemaining,
          expired: result.expired,
          newCodeSent: result.newCodeSent
        };
      }
    } catch (err) {
      console.error('[DEBUG] AuthStore.verifyCodeAndLogin: Error occurred:', err);
      const structuredError = errorHandler.handleError(err, { action: 'verifyCodeAndLogin' });
      setError(structuredError);
      return {
        success: false,
        error: structuredError.getUserMessage ? structuredError.getUserMessage() : structuredError.message
      };
    } finally {
      isLoading.value = false;
      // Final check of token state
      const finalToken = localStorage.getItem('accessToken');
      console.log('[DEBUG] AuthStore.verifyCodeAndLogin: Final token state on exit:', {
        accessTokenPresent: !!finalToken,
        accessTokenPreview: finalToken ? finalToken.substring(0, 20) + '...' : null
      });
    }
  };

  /**
   * Resend verification code
   * @param {string} email - User email address
   */
  const resendVerificationCode = async (email) => {
    isLoading.value = true;
    error.value = null;

    try {
      const result = await verificationService.resendVerificationCode(email);
      
      if (result.success) {
        return {
          success: true,
          message: result.message,
          codeExpiresAt: result.codeExpiresAt,
          nextResendAvailable: result.nextResendAvailable
        };
      } else {
        setError(result.error);
        return {
          success: false,
          error: result.error,
          code: result.code,
          nextResendAvailable: result.nextResendAvailable
        };
      }
    } catch (err) {
      const structuredError = errorHandler.handleError(err, { action: 'resendVerificationCode' });
      setError(structuredError);
      return {
        success: false,
        error: structuredError.getUserMessage ? structuredError.getUserMessage() : structuredError.message
      };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Clear all authentication data
   */
  const clearAuth = () => {
    clearTokens();
    clearUser();
    error.value = null;
  };

  /**
   * Get auth headers for API requests
   */
  const getAuthHeaders = () => {
    if (!accessToken.value) {
      return {};
    }

    return {
      Authorization: `Bearer ${accessToken.value}`
    };
  };

  // Initialize on store creation
  initialize();

  return {
    // State
    user,
    accessToken,
    refreshToken,
    isLoading,
    error,
    
    // Getters
    isAuthenticated,
    currentUser,
    authError,
    needsVerification,
    
    // Actions
    register,
    login,
    logout,
    refresh,
    getCurrentUser,
    updateProfile,
    changePassword,
    requestPasswordReset,
    resetPassword,
    verifyEmail,
    resendVerificationEmail,
    checkVerificationStatus,
    processVerificationFromUrl,
    verifyCodeAndLogin,
    resendVerificationCode,
    clearAuth,
    getAuthHeaders,
    setError,
    setTokens,
    setUser,
    initialize,
    getTimeUntilNextResend
  };
});
